// ProjectPage.tsx

import React, { ReactNode, useState } from "react";
import Carousel from "react-slick";

import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { FiChevronLeft, FiChevronRight } from "react-icons/fi"; // Assuming you have Feather icons installed

import {
  DisseminationItem,
  Dissemination,
  Project,
} from "../../shared/interfaces";

import projects from "./popolateProjects";
import { useExpandedProject } from "../../Components/ExpandableProjects";
interface ArrowProps {
  onClick?: () => void;
}

interface ProjectDetailsProps {
  isVisible: boolean;
  children: ReactNode;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  isVisible,
  children,
}) => (
  <div
    className={`mt-4 mb-2 text-gray-950 transition-opacity duration-1000 ${
      isVisible ? "opacity-100" : "opacity-0 "
    }`}
  >
    {isVisible && children}
  </div>
);

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { expandedProject, setExpandedProject } = useExpandedProject();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // ... (previous code)

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  const toggleProject = (projectId: string) => {
    setExpandedProject((prevProject) =>
      prevProject === projectId ? null : projectId
    );
  };

  const renderDisseminationItemItem = (dissemination: DisseminationItem) => (
    <div>
      <ul className="list-disc lg:px-16 pl-6">
        <li>
          <p className="text-palette1 text-justify mt-8">
            {dissemination.description}
          </p>
          {dissemination.link && (
            <a
              className="text-palette3 mt-4 hover:underline inline-block"
              href={dissemination.link}
              target="_new"
            >
              Read Article
            </a>
          )}
        </li>
      </ul>
    </div>
  );

  const formatTitle = (title: string) => {
    return title.split("").map((letter, index) => {
      const isUpperCase = /[A-Z]/.test(letter);
      return (
        <span
          key={index}
          className={`${isUpperCase ? "font-bold text-palette3" : ""}`}
        >
          {letter}
        </span>
      );
    });
  };

  const renderDisseminationItem = (dissemination: Dissemination) =>
    dissemination.item.map(renderDisseminationItemItem);

  const renderProjectItem = (project: Project) => (
    <div
      key={project.id}
      className={`p-4 lg:p-8 lg:px-16 shadow-md mb-4 rounded-lg ${
        expandedProject === project.id ? "bg-palette5 " : "bg-palette5  "
      }`}
    >
      <div
        className="flex flex-col lg:flex-row justify-between items-center cursor-pointer"
        onClick={() => toggleProject(project.id)}
      >
        {" "}
        <img
          src={project.logo}
          alt={`project-${project.id}-logo`}
          className="w-64 h-64 mr-2 object-contain rounded-xl bg-palette5"
        />
        <div className="flex flex-col  items-center lg:items-start ">
          <h2
            className={` mb-2 lg:ml-4  font-bold text-palette3  ${
              expandedProject === project.id
                ? "text-palette2 text-4xl lg:text-6xl hidden"
                : "text-palette1 text-2xl lg:text-4xl "
            }`}
          >
            {project.shortTitle}
          </h2>
          <h2
            className={` mb-2 lg:ml-4 text-xl lg:text-2xl text-center lg:text-start ${
              expandedProject === project.id
                ? "text-palette2 hidden"
                : "text-palette1"
            }`}
          >
            {formatTitle(project.title)}
          </h2>
          {expandedProject !== project.id && (
            <p className="text-palette1 mb-2 lg:mx-10 lg:ml-4  lg:text-xl text-justify">
              {project.shortDescription}
            </p>
          )}
        </div>
        <span
          className={`${
            expandedProject === project.id ? "text-palette2" : "text-palette3"
          }`}
        >
          {expandedProject === project.id ? "Hide" : "Show Details"}
        </span>
      </div>

      <ProjectDetails isVisible={expandedProject === project.id}>
        <p className="mt-2  mb-12  text-center px-16 lg:text-6xl font-bold text-4xl text-palette3">
          {project.shortTitle}
        </p>
        <p className="3xl:text-7xl  font-bold text-4xl mt-24 text-center  text-palette1 mb-6 lg:mb-12 ">
          {formatTitle(project.title)}
        </p>

        {project.description.split("\n").map((line, index) => (
          <p
            key={index}
            className="mt-2 mb-4 lg:px-16 text-justify lg:text-xl text-gray-950"
          >
            {line}
          </p>
        ))}
        {project.images.length > 0 && (
          <Carousel
            arrows={project.images.length > 1}
            prevArrow={<PrevArrow />}
            nextArrow={<NextArrow />}
          >
            {project.images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`project-${project.id}-image-${index}`}
                className="w-auto object-contain h-48 mb-2"
                onClick={() => handleImageClick(image)}
              />
            ))}
          </Carousel>
        )}
        {expandedProject === project.id && project.home != "" && (
          <div className="flex justify-center mt-8">
            <a
              href={project.home}
              target="_new"
              className="text-palette3 mt-4 hover:underline inline-block"
            >
              Home Page
            </a>
          </div>
        )}
        {expandedProject === project.id &&
          project.disseminationLink.map(renderDisseminationItem)}
        {expandedProject === project.id && (
          <div className="flex justify-center mt-8">
            <button className="mt-4 mb-8 mx-8 bg-palette border border-palette3 text-palette3 px-4 py-2 rounded hover:bg-palette3 hover:text-palette5 transition-colors">
              <a href={project.link} target="_new">
                Read More
              </a>
            </button>
          </div>
        )}
      </ProjectDetails>
    </div>
  );

  const PrevArrow: React.FC<ArrowProps> = ({ onClick }) => (
    <div
      className="absolute top-1/2 left-4 cursor-pointer text-palette1 z-10"
      onClick={() => {
        console.log("Back arrow clicked");
        onClick?.();
      }}
    >
      <FiChevronLeft size={24} />
    </div>
  );

  const NextArrow: React.FC<ArrowProps> = ({ onClick }) => (
    <div
      className="absolute top-1/2 right-4 cursor-pointer text-palette1 z-10"
      onClick={() => {
        console.log("Next arrow clicked");
        onClick?.();
      }}
    >
      <FiChevronRight size={24} />
    </div>
  );

  return (
    <main className=" bg-gray-100 w-full h-full sm:h-full pb-8 2xl:h-full 2xl:py-40 flex flex-col justify-center items-center px-4 lg:px-16 ">
      <div className="container mx-auto text-center mb-8">
        <h1 className="2xl:text-7xl  lg:text-6xl  font-bold text-4xl mt-24 text-center  text-palette1 mb-6 lg:mb-6">
          Our Projects
        </h1>
        <p className="text-gray-950 2xl:text-4xl lg:text-xl ">
          Delve into Dream Innovation's Project Portfolio
        </p>
      </div>
      <div className={`container  mx-auto rounded-lg shadow-lg text-justify `}>
        {projects.map(renderProjectItem)}
      </div>
      {selectedImage && (
        <div
          className="fixed top-0 left-0 w-full h-full z-50 bg-palette1 bg-opacity-80 flex items-center justify-center"
          onClick={closeImagePopup}
        >
          <img src={selectedImage} alt="popup" className="h-[calc(70vh)]" />
        </div>
      )}
    </main>
  );
};

export default ProjectPage;
